import { useEffect, useMemo, useState, type ComponentType } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  CalendarClock,
  FilePlus2,
  FileText,
  Loader2,
  Plus,
  Sparkles,
  Timer,
  Trash2,
  WifiOff,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { TooltipProvider } from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { MarketingPageShell } from './components/MarketingPageShell';
import { ActionIconButton } from './components/ActionIconButton';
import { EmptyState, StatusBadge } from './components/marketing-ui';
import {
  useCreateSeriesMutation,
  useDeleteSeriesMutation,
  usePlaysQuery,
  useSeriesDueQuery,
  useSeriesQuery,
  useUpdateSeriesMutation,
} from '@/client/api/marketing/plays';
import { useCreateContentMutation } from '@/client/api/marketing/studio';
import type { ApiSeries } from '@/client/types/marketing/api';
import { getApiErrorMessage } from '@/client/utils/errors';
import {
  FieldHint,
  RequiredMark,
  clearFieldError,
  fieldClass,
} from '@/client/pages/Admin/shared/form-field';
import {
  MARKETING_VERTICAL_LABEL,
  MARKETING_VERTICALS,
  SERIES_CADENCE_LABEL,
  SERIES_LAYER_LABEL,
  type MarketingVertical,
  type SeriesLayer,
} from '@/client/types/marketing/strategy';
import { CONTENT_TYPE_LABEL, type ContentType } from '@/client/types/marketing';

/**
 * Studio → Series, on real data.
 *
 * A series is what causes content to exist: the PRD's calendar only displays
 * what someone already made. Two counts are shown per series and they are not
 * the same fact — `editions_published` is how many reached published,
 * `content_count` is how many drafts it has spawned. Eight drafts and one
 * published is a cadence problem; showing only one number hides it.
 *
 * "Draft edition" creates a real content item with `series_id` set and navigates
 * to the editor. The beat runner does the same thing for due series on its own
 * schedule; this is the manual escape hatch for "I want next week's now".
 */

const inputClass =
  'h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm outline-none ring-blue-500 focus:ring-2';

const LAYER_TONE: Record<string, string> = {
  news: 'bg-sky-50 text-sky-700 border-sky-200',
  research: 'bg-violet-50 text-violet-700 border-violet-200',
  evergreen: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  social: 'bg-amber-50 text-amber-700 border-amber-200',
};

const fmtDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString() : '—');

const CONTENT_TYPES = Object.keys(CONTENT_TYPE_LABEL) as ContentType[];

function InsightTile({
  label,
  value,
  meta,
  bar,
  icon: Icon,
  tone,
  onClick,
}: {
  label: string;
  value: string;
  meta?: string[];
  bar?: { value: number; className: string };
  icon: ComponentType<{ className?: string }>;
  tone: string;
  onClick?: () => void;
}) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={cn(
        'flex h-full min-w-0 flex-col rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-left',
        onClick && 'transition hover:border-blue-300 hover:bg-blue-50/40',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
          {label}
        </span>
        <span className={cn('flex h-6 w-6 items-center justify-center rounded-md', tone)}>
          <Icon className="h-3.5 w-3.5" />
        </span>
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums leading-none text-gray-900">
        {value}
      </div>
      {bar && (
        <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-gray-100">
          <div
            className={cn('h-full rounded-full', bar.className)}
            style={{ width: `${Math.min(100, Math.max(0, bar.value * 100))}%` }}
          />
        </div>
      )}
      {meta && meta.length > 0 && (
        <p
          className="mt-1.5 truncate text-[11px] leading-snug text-gray-500"
          title={meta.join(' · ')}
        >
          {meta.join(' · ')}
        </p>
      )}
    </Tag>
  );
}

interface SeriesFormState {
  name: string;
  layer: SeriesLayer;
  cadence: string;
  input_kind: 'manual' | 'marketplace_signal';
  signal_query: string;
  vertical: MarketingVertical | '';
  play_id: string;
  content_type: ContentType;
  template_body: string;
  next_due_at: string;
}

type SeriesFormErrors = Partial<Record<keyof SeriesFormState, string>>;

const emptyForm: SeriesFormState = {
  name: '',
  layer: 'news',
  cadence: 'weekly',
  input_kind: 'manual',
  signal_query: '',
  vertical: '',
  play_id: '',
  content_type: 'blog_post',
  template_body: '',
  next_due_at: '',
};

export default function SeriesPage() {
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useSeriesQuery({ limit: 200, with_counts: 1 });
  const due = useSeriesDueQuery(14);
  const plays = usePlaysQuery({ limit: 100 });
  const createSeries = useCreateSeriesMutation();
  const updateSeries = useUpdateSeriesMutation();
  const deleteSeries = useDeleteSeriesMutation();
  const createContent = useCreateContentMutation();

  const [layer, setLayer] = useState<SeriesLayer | 'all'>('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<SeriesFormState>(emptyForm);
  const [formErrors, setFormErrors] = useState<SeriesFormErrors>({});
  const [drafting, setDrafting] = useState<number | null>(null);

  const patchForm = <K extends keyof SeriesFormState>(key: K, value: SeriesFormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setFormErrors((e) => clearFieldError(e, key));
  };

  const series: ApiSeries[] = data?.items ?? [];
  const dueList = due.data?.items ?? [];
  const playList = plays.data?.items ?? [];

  const filtered = useMemo(
    () => (layer === 'all' ? series : series.filter((s) => s.layer === layer)),
    [series, layer],
  );

  const insights = useMemo(() => {
    const activeCount = series.filter((s) => s.active).length;
    const autoCount = series.filter((s) => s.input_kind === 'marketplace_signal').length;
    const linkedEditions = series.reduce((sum, s) => sum + (s.content_count ?? 0), 0);
    const orphanSeries = series.filter((s) => s.active && (s.content_count ?? 0) === 0).length;
    const duePct = series.length ? dueList.length / series.length : 0;

    const callouts: { tone: string; text: string }[] = [];
    if (dueList.length > 0) {
      callouts.push({
        tone: 'border-sky-200 bg-sky-50 text-sky-900',
        text: `${dueList.length} series due in the next 14 days — draft the next edition into Content.`,
      });
    }
    if (orphanSeries > 0) {
      callouts.push({
        tone: 'border-amber-200 bg-amber-50 text-amber-900',
        text: `${orphanSeries} active series ${orphanSeries === 1 ? 'has' : 'have'} no Content editions yet.`,
      });
    }
    if (autoCount > 0) {
      callouts.push({
        tone: 'border-violet-200 bg-violet-50 text-violet-900',
        text: `${autoCount} auto-signal series can draft from marketplace activity instead of a blank page.`,
      });
    }
    if (callouts.length === 0 && series.length > 0) {
      callouts.push({
        tone: 'border-emerald-200 bg-emerald-50 text-emerald-900',
        text: 'Cadence looks healthy — keep drafting editions into Content on schedule.',
      });
    }

    return { activeCount, autoCount, linkedEditions, orphanSeries, duePct, callouts };
  }, [series, dueList.length]);

  useEffect(() => {
    if (layer !== 'all' && filtered.length === 0) setLayer('all');
  }, [layer, filtered.length]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors: SeriesFormErrors = {};
    const name = form.name.trim();
    if (!name) {
      errors.name = 'Name is required';
    } else if (name.length < 3) {
      errors.name = 'Use at least 3 characters';
    }
    if (form.input_kind === 'marketplace_signal' && !form.signal_query.trim()) {
      // An auto-signal series with no query drafts from nothing, and looks like
      // a broken runner rather than a half-filled form.
      errors.signal_query = 'A marketplace query is required for auto-signal series';
    }
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast.error('Fix the highlighted fields before saving');
      return;
    }

    createSeries.mutate(
      {
        name: form.name.trim(),
        layer: form.layer,
        cadence: form.cadence,
        input_kind: form.input_kind,
        signal_query: form.signal_query.trim() || null,
        vertical: form.vertical || null,
        play_id: form.play_id ? Number(form.play_id) : null,
        content_type: form.content_type,
        template_body: form.template_body.trim() || null,
        next_due_at: form.next_due_at || null,
        active: 1,
      },
      {
        onSuccess: () => {
          toast.success(`Created “${form.name.trim()}”`);
          setForm(emptyForm);
          setFormErrors({});
          setDialogOpen(false);
        },
        onError: (err) => toast.error(getApiErrorMessage(err, 'Could not create the series')),
      },
    );
  };

  const patch = (s: ApiSeries, changes: Record<string, unknown>) => {
    updateSeries.mutate(
      { series_id: s.series_id, ...changes },
      { onError: (err) => toast.error(getApiErrorMessage(err, 'Could not update the series')) },
    );
  };

  /**
   * Materialise one edition now.
   *
   * `status` is not sent: it is not writable, and a new item starts as a draft
   * anyway. Sending it would be silently dropped, which is worse than not
   * sending it — it reads like it worked.
   */
  const draftEdition = (s: ApiSeries) => {
    const contentType = (
      CONTENT_TYPES.includes(s.content_type as ContentType) ? s.content_type : 'blog_post'
    ) as ContentType;
    const dueLabel = s.next_due_at ? new Date(s.next_due_at).toLocaleDateString() : 'next edition';

    setDrafting(s.series_id);
    createContent.mutate(
      {
        title: `${s.name} — ${dueLabel}`,
        content_type: contentType,
        play_id: s.play_id,
        series_id: s.series_id,
        body: s.template_body ?? '',
        excerpt:
          s.input_kind === 'marketplace_signal' && s.signal_query
            ? `Auto-signal draft from: ${s.signal_query}`
            : `Edition drafted from series “${s.name}”.`,
        tags: s.vertical ? [s.vertical, 'series'] : ['series'],
      },
      {
        onSuccess: (created) => {
          const mciid = created?.data?.mciid;
          toast.success('Edition drafted into Content');
          if (mciid) navigate(`/admin/marketing/studio/content/${mciid}`);
          else navigate(`/admin/marketing/studio/content?series=${s.series_id}`);
        },
        onError: (err) => toast.error(getApiErrorMessage(err, 'Could not draft the edition')),
        onSettled: () => setDrafting(null),
      },
    );
  };

  if (isError) {
    return (
      <MarketingPageShell
        title="Series"
        description="Cadence templates that spawn Content editions — so each week starts from a rhythm, not a blank page."
        crumbs={[
          { label: 'Marketing', href: '/admin/marketing' },
          { label: 'Studio' },
          { label: 'Series' },
        ]}
        icon={Timer}
      >
        <div className="rounded-xl border border-amber-100 bg-amber-50/40 px-6 py-16 text-center text-sm shadow-sm">
          <WifiOff className="mb-2 inline h-5 w-5 text-amber-600" />
          <div className="font-medium text-amber-800">Could not load series</div>
          <div className="mt-1 text-xs text-gray-500">
            {getApiErrorMessage(error, 'The marketing API did not respond')}
          </div>
        </div>
      </MarketingPageShell>
    );
  }

  return (
    <MarketingPageShell
      title="Series"
      description="Cadence templates that spawn Content editions — so each week starts from a rhythm, not a blank page."
      crumbs={[
        { label: 'Marketing', href: '/admin/marketing' },
        { label: 'Studio' },
        { label: 'Series' },
      ]}
      icon={Timer}
      actions={
        <Button type="button" className="gap-2" onClick={() => setDialogOpen(true)}>
          <Plus className="h-4 w-4" /> New series
        </Button>
      }
    >
      {isLoading ? (
        <div className="rounded-xl border border-gray-100 bg-white px-6 py-16 text-center text-sm text-gray-500 shadow-sm">
          <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
          Loading series…
        </div>
      ) : (
        <>
          <div className="mb-5 space-y-3">
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <InsightTile
                label="Series"
                value={String(series.length)}
                meta={[`${insights.activeCount} active`, `${insights.autoCount} auto-signal`]}
                icon={Timer}
                tone="bg-blue-50 text-blue-700"
                onClick={() => setLayer('all')}
              />
              <InsightTile
                label="Due soon"
                value={String(dueList.length)}
                meta={['next 14 days']}
                bar={{
                  value: insights.duePct,
                  className: dueList.length > 0 ? 'bg-sky-500' : 'bg-emerald-500',
                }}
                icon={CalendarClock}
                tone={
                  dueList.length > 0 ? 'bg-sky-50 text-sky-700' : 'bg-emerald-50 text-emerald-700'
                }
              />
              <InsightTile
                label="Content editions"
                value={String(insights.linkedEditions)}
                meta={[`${insights.orphanSeries} series with none yet`]}
                icon={FileText}
                tone="bg-violet-50 text-violet-700"
                onClick={() => navigate('/admin/marketing/studio/content')}
              />
              <InsightTile
                label="Auto-signal"
                value={String(insights.autoCount)}
                meta={['marketplace-fed drafts']}
                icon={Sparkles}
                tone="bg-amber-50 text-amber-700"
              />
            </div>

            <div className="grid gap-3 lg:grid-cols-[1.4fr_1fr]">
              <div className="rounded-lg border border-gray-200 bg-white px-3 py-2.5">
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                  Insights
                </p>
                {insights.callouts.length === 0 ? (
                  <p className="text-xs text-gray-400">
                    No series yet — create one to give the week a rhythm.
                  </p>
                ) : (
                  <ul className="space-y-1.5">
                    {insights.callouts.map((c) => (
                      <li
                        key={c.text}
                        className={cn('rounded-md border px-2.5 py-1.5 text-xs leading-snug', c.tone)}
                      >
                        {c.text}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="rounded-lg border border-gray-200 bg-white px-3 py-2.5">
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                  How it ties to Content
                </p>
                <p className="text-xs leading-relaxed text-gray-600">
                  A series is the template (cadence, type, play).{' '}
                  <span className="font-medium text-gray-800">Draft edition</span> creates a Content
                  item with <code className="text-[11px]">series_id</code> set. The beat runner does
                  the same for due series on its own schedule — this is the manual escape hatch.
                </p>
              </div>
            </div>
          </div>

          {dueList.length > 0 && (
            <div className="mb-4 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2.5">
              <div className="mb-1.5 flex items-center gap-2 text-xs font-semibold text-sky-900">
                <CalendarClock className="h-3.5 w-3.5" />
                Next editions due
              </div>
              <ul className="space-y-1 text-xs text-sky-900">
                {dueList.map((s) => (
                  <li key={s.series_id} className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{s.name}</span>
                    <span className="text-sky-700">— {fmtDate(s.next_due_at)}</span>
                    {s.input_kind === 'marketplace_signal' && (
                      <span className="inline-flex items-center gap-1 rounded bg-sky-100 px-1.5 py-0.5 text-[10px] text-sky-800">
                        <Sparkles className="h-3 w-3" /> auto-drafts
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => draftEdition(s)}
                      disabled={drafting === s.series_id}
                      className="rounded border border-sky-300 bg-white px-1.5 py-0.5 text-[10px] font-medium text-sky-800 hover:bg-sky-100 disabled:opacity-50"
                    >
                      {drafting === s.series_id ? 'Drafting…' : 'Draft now'}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mb-3 flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => setLayer('all')}
              className={cn(
                'rounded-md border px-2.5 py-1 text-xs',
                layer === 'all'
                  ? 'border-blue-400 bg-blue-50 text-blue-700'
                  : 'border-gray-200 text-gray-600',
              )}
            >
              All layers
            </button>
            {(Object.keys(SERIES_LAYER_LABEL) as SeriesLayer[]).map((l) => (
              <button
                type="button"
                key={l}
                onClick={() => setLayer(l)}
                className={cn(
                  'rounded-md border px-2.5 py-1 text-xs',
                  layer === l
                    ? 'border-blue-400 bg-blue-50 text-blue-700'
                    : 'border-gray-200 text-gray-600',
                )}
              >
                {SERIES_LAYER_LABEL[l]}
              </button>
            ))}
          </div>

          <div className="space-y-2">
            <TooltipProvider delayDuration={200}>
            {series.length === 0 && (
              <EmptyState label="No series yet — create one so each week starts from a rhythm rather than a blank page." />
            )}
            {series.length > 0 && filtered.length === 0 && (
              <EmptyState label="No series in this layer" />
            )}
            {filtered.map((s) => {
              const editionCount = s.content_count ?? 0;
              return (
                <div
                  key={s.series_id}
                  className={cn(
                    'rounded-lg border bg-white px-4 py-3',
                    s.active ? 'border-gray-200' : 'border-gray-200 opacity-60',
                  )}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-sm font-semibold text-gray-900">{s.name}</h3>
                        <StatusBadge
                          label={SERIES_LAYER_LABEL[s.layer as SeriesLayer] ?? s.layer}
                          className={LAYER_TONE[s.layer] ?? 'bg-gray-100 text-gray-700'}
                        />
                        <span className="rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[10px] text-gray-600">
                          {SERIES_CADENCE_LABEL[
                            s.cadence as keyof typeof SERIES_CADENCE_LABEL
                          ] ?? s.cadence}
                        </span>
                        {s.input_kind === 'marketplace_signal' && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-[10px] font-medium text-violet-700">
                            <Sparkles className="h-3 w-3" /> auto-signal
                          </span>
                        )}
                      </div>

                      {s.signal_query && (
                        <code className="mt-2 block overflow-x-auto rounded bg-gray-900 px-2.5 py-1.5 text-[10px] text-gray-100">
                          {s.signal_query}
                        </code>
                      )}

                      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-500">
                        <span>Next due {fmtDate(s.next_due_at)}</span>
                        <span>Last published {fmtDate(s.last_published_at)}</span>
                        <span>
                          {editionCount} Content edition{editionCount === 1 ? '' : 's'}
                          {s.editions_published > 0
                            ? ` · ${s.editions_published} published`
                            : ''}
                        </span>
                        {s.vertical && <span>Vertical: {s.vertical}</span>}
                        <span>
                          Type:{' '}
                          {CONTENT_TYPE_LABEL[s.content_type as ContentType] ?? s.content_type}
                        </span>
                      </div>

                      <div className="mt-2.5 flex flex-wrap items-center gap-2">
                        <label className="text-[11px] text-gray-500">Play</label>
                        <select
                          value={s.play_id != null ? String(s.play_id) : ''}
                          onChange={(e) =>
                            patch(s, {
                              play_id: e.target.value ? Number(e.target.value) : null,
                            })
                          }
                          className="h-8 rounded-md border border-gray-200 bg-white px-2 text-xs outline-none ring-blue-500 focus:ring-2"
                        >
                          <option value="">No play linked</option>
                          {playList.map((p) => (
                            <option key={p.play_id} value={String(p.play_id)}>
                              {p.name}
                            </option>
                          ))}
                        </select>
                        <Link
                          to={`/admin/marketing/studio/content?series=${s.series_id}`}
                          className="text-[11px] font-medium text-blue-700 hover:underline"
                        >
                          {editionCount > 0
                            ? `View ${editionCount} edition${editionCount === 1 ? '' : 's'} in Content →`
                            : 'Open in Content →'}
                        </Link>
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 gap-1.5 border-blue-300 bg-blue-50 text-xs text-blue-800 hover:bg-blue-100"
                        onClick={() => draftEdition(s)}
                        disabled={drafting === s.series_id}
                      >
                        <FilePlus2 className="h-3.5 w-3.5" />
                        {drafting === s.series_id ? 'Drafting…' : 'Draft edition'}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className={cn(
                          'h-8 text-xs',
                          !s.active &&
                            'border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
                        )}
                        onClick={() => patch(s, { active: s.active ? 0 : 1 })}
                      >
                        {s.active ? 'Pause' : 'Activate'}
                      </Button>
                      <ActionIconButton
                        label="Delete"
                        onClick={() =>
                          deleteSeries.mutate(
                            { series_id: s.series_id },
                            {
                              onSuccess: () => toast.success('Series removed'),
                              onError: (err) =>
                                toast.error(
                                  getApiErrorMessage(err, 'Could not remove the series'),
                                ),
                            },
                          )
                        }
                        className="border border-gray-200 text-gray-500 hover:bg-red-50 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </ActionIconButton>
                    </div>
                  </div>
                </div>
              );
            })}
            </TooltipProvider>
          </div>
        </>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>New series</DialogTitle>
            <DialogDescription>
              A cadence template. The runner materialises a draft each time one comes due, so the
              week starts from a rhythm rather than a blank page.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} noValidate className="space-y-4">
            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-gray-600">
                Name <RequiredMark />
              </span>
              <input
                value={form.name}
                onChange={(e) => patchForm('name', e.target.value)}
                className={fieldClass(inputClass, formErrors.name)}
                aria-invalid={!!formErrors.name}
                placeholder="Accra rental price watch"
              />
              <FieldHint error={formErrors.name} />
            </label>

            <div className="grid gap-3 sm:grid-cols-3">
              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-gray-600">Layer</span>
                <select
                  value={form.layer}
                  onChange={(e) => setForm((f) => ({ ...f, layer: e.target.value as SeriesLayer }))}
                  className={inputClass}
                >
                  {(Object.keys(SERIES_LAYER_LABEL) as SeriesLayer[]).map((l) => (
                    <option key={l} value={l}>
                      {SERIES_LAYER_LABEL[l]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-gray-600">Cadence</span>
                <select
                  value={form.cadence}
                  onChange={(e) => setForm((f) => ({ ...f, cadence: e.target.value }))}
                  className={inputClass}
                >
                  {Object.entries(SERIES_CADENCE_LABEL).map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-gray-600">Drafts as</span>
                <select
                  value={form.content_type}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, content_type: e.target.value as ContentType }))
                  }
                  className={inputClass}
                >
                  {CONTENT_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {CONTENT_TYPE_LABEL[t]}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-gray-600">Input</span>
              <select
                value={form.input_kind}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    input_kind: e.target.value as 'manual' | 'marketplace_signal',
                  }))
                }
                className={inputClass}
              >
                <option value="manual">Manual — someone writes each edition</option>
                <option value="marketplace_signal">
                  Auto-signal — draft from marketplace activity
                </option>
              </select>
            </label>

            {form.input_kind === 'marketplace_signal' && (
              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-gray-600">
                  Marketplace query <RequiredMark />
                </span>
                <input
                  value={form.signal_query}
                  onChange={(e) => patchForm('signal_query', e.target.value)}
                  className={fieldClass(inputClass, formErrors.signal_query)}
                  aria-invalid={!!formErrors.signal_query}
                  placeholder="homes in Accra listed this week, verified only"
                />
                <FieldHint error={formErrors.signal_query} />
                <span className="mt-1 block text-[11px] text-gray-400">
                  What each edition is drafted from. This is the part no standalone marketing tool
                  can do.
                </span>
              </label>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-gray-600">Vertical</span>
                <select
                  value={form.vertical}
                  onChange={(e) =>
                    patchForm('vertical', e.target.value as MarketingVertical | '')
                  }
                  className={inputClass}
                >
                  <option value="">None</option>
                  {MARKETING_VERTICALS.map((v) => (
                    <option key={v} value={v}>
                      {MARKETING_VERTICAL_LABEL[v]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-gray-600">First edition due</span>
                <input
                  type="date"
                  value={form.next_due_at}
                  onChange={(e) => setForm((f) => ({ ...f, next_due_at: e.target.value }))}
                  className={inputClass}
                />
              </label>
            </div>

            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-gray-600">Play</span>
              <select
                value={form.play_id}
                onChange={(e) => setForm((f) => ({ ...f, play_id: e.target.value }))}
                className={inputClass}
              >
                <option value="">No play linked</option>
                {playList.map((p) => (
                  <option key={p.play_id} value={String(p.play_id)}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-gray-600">Template body</span>
              <textarea
                value={form.template_body}
                onChange={(e) => setForm((f) => ({ ...f, template_body: e.target.value }))}
                className="min-h-[80px] w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm outline-none ring-blue-500 focus:ring-2"
                placeholder="The skeleton every edition starts from — sections, standing questions, the usual closing CTA."
              />
            </label>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                disabled={createSeries.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createSeries.isPending}>
                {createSeries.isPending ? 'Creating…' : 'Create series'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </MarketingPageShell>
  );
}
